import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
} from '@mui/material';
import {
  StyledCrmDialog,
  CrmDialogTitle,
  CrmDialogCloseButton,
  CrmDialogContent,
  CrmDialogActions,
  CrmField,
  CrmPrimaryButton,
  CrmSecondaryButton,
  CrmPanel,
  CrmSectionTitle,
} from './AdminCrm.styled';

function slugify(label) {
  return String(label || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function cloneSettings(settings) {
  return {
    trades: (settings?.trades || []).map((t) => ({ ...t })),
    pipelineStages: (settings?.pipelineStages || []).map((s) => ({ ...s })),
    outreachChannels: (settings?.outreachChannels || []).map((c) => ({ ...c })),
    relationshipManagers: (settings?.relationshipManagers || []).map((r) => ({
      ...r,
    })),
  };
}

function LookupEditor({ title, items, onChange, showCountsTowardTotal = false }) {
  const updateItem = (index, patch) => {
    const next = items.map((item, i) =>
      i === index ? { ...item, ...patch } : item,
    );
    onChange(next);
  };

  const addItem = () => {
    const sortOrder =
      items.reduce((max, item) => Math.max(max, item.sortOrder || 0), 0) + 1;
    const next = [
      ...items,
      {
        id: '',
        label: '',
        sortOrder,
        active: true,
        ...(showCountsTowardTotal ? { countsTowardTotal: true } : {}),
      },
    ];
    onChange(next);
  };

  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index));
  };

  return (
    <CrmPanel>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <CrmSectionTitle variant="subtitle1">{title}</CrmSectionTitle>
        <CrmSecondaryButton
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={addItem}
        >
          Add
        </CrmSecondaryButton>
      </Box>
      {items.map((item, index) => (
        <Box
          key={`${title}-${index}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: showCountsTowardTotal
              ? '1.2fr 1.4fr 80px auto auto auto'
              : '1.2fr 1.4fr 80px auto auto',
            gap: 1,
            alignItems: 'center',
            mb: 1,
          }}
        >
          <CrmField
            size="small"
            label="Id"
            value={item.id}
            onChange={(e) => updateItem(index, { id: e.target.value.trim() })}
            onBlur={() => {
              if (!item.id && item.label) {
                updateItem(index, { id: slugify(item.label) });
              }
            }}
          />
          <CrmField
            size="small"
            label="Label"
            value={item.label}
            onChange={(e) => {
              const label = e.target.value;
              const patch = { label };
              if (!item.id) patch.id = slugify(label);
              updateItem(index, patch);
            }}
          />
          <CrmField
            size="small"
            label="Order"
            type="number"
            value={item.sortOrder}
            onChange={(e) =>
              updateItem(index, { sortOrder: Number(e.target.value) || 0 })
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={item.active !== false}
                onChange={(e) => updateItem(index, { active: e.target.checked })}
                size="small"
              />
            }
            label="Active"
            sx={{ m: 0 }}
          />
          {showCountsTowardTotal && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={item.countsTowardTotal !== false}
                  onChange={(e) =>
                    updateItem(index, { countsTowardTotal: e.target.checked })
                  }
                  size="small"
                />
              }
              label="In total"
              sx={{ m: 0 }}
            />
          )}
          <IconButton
            aria-label={`Remove ${title} item`}
            onClick={() => removeItem(index)}
            size="small"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      {items.length === 0 && (
        <Typography variant="body2" sx={{ color: '#5a6478' }}>
          No items yet.
        </Typography>
      )}
    </CrmPanel>
  );
}

function AdminCrmSettingsModal({
  open,
  settings,
  onClose,
  onSave,
  saving = false,
}) {
  const [draft, setDraft] = useState(cloneSettings(settings));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft(cloneSettings(settings));
      setError(null);
    }
  }, [open, settings]);

  const handleSave = async () => {
    setError(null);
    try {
      await onSave(draft);
    } catch (e) {
      setError(e.message || 'Failed to save settings');
    }
  };

  return (
    <StyledCrmDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="admin-crm-settings-title"
    >
      <CrmDialogTitle id="admin-crm-settings-title">
        <span className="crm-dialog-icon-wrap" aria-hidden>
          <SettingsOutlinedIcon sx={{ color: '#0d4efe' }} />
        </span>
        CRM settings
        <CrmDialogCloseButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </CrmDialogCloseButton>
      </CrmDialogTitle>

      <CrmDialogContent>
        {error && (
          <Alert severity="error" sx={{ borderRadius: '10px' }}>
            {error}
          </Alert>
        )}
        <LookupEditor
          title="Trades"
          items={draft.trades}
          onChange={(trades) => setDraft((d) => ({ ...d, trades }))}
        />
        <LookupEditor
          title="Pipeline stages"
          items={draft.pipelineStages}
          onChange={(pipelineStages) =>
            setDraft((d) => ({ ...d, pipelineStages }))
          }
          showCountsTowardTotal
        />
        <LookupEditor
          title="Outreach channels"
          items={draft.outreachChannels}
          onChange={(outreachChannels) =>
            setDraft((d) => ({ ...d, outreachChannels }))
          }
        />
        <LookupEditor
          title="Relationship managers"
          items={draft.relationshipManagers}
          onChange={(relationshipManagers) =>
            setDraft((d) => ({ ...d, relationshipManagers }))
          }
        />
      </CrmDialogContent>

      <CrmDialogActions>
        <CrmSecondaryButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </CrmSecondaryButton>
        <CrmPrimaryButton onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </CrmPrimaryButton>
      </CrmDialogActions>
    </StyledCrmDialog>
  );
}

export default AdminCrmSettingsModal;
